import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import MainLayout from '../../../layouts/MainLayout';
import { Button } from '../../../components/ui/Button/Button';
import { Alert } from '../../../components/ui/Alert/Alert';
import { authService, protocoloService, userProfileService, pessoaService } from '../../../services';
import logger from '../../../utils/logger';
import { getPedidoConfig, getDepartamento } from '../config';
import { usePedidoForm } from '../core/hooks/usePedidoForm';
import { describePaymentSelection } from '../core/services/pagamentoService';
import { resolveProtocoloDados } from '../core/services/protocoloResolverService';
import { criarRascunhoRequerimento, marcarAguardandoPagamento, finalizarRequerimentoSemTaxa, cancelarRequerimento, isRascunhoAberto } from '../core/services/protocoloRequerimentoService';
import { getPessoaIdRascunho, obterRascunho, salvarRascunho, removerRascunho } from '../core/services/rascunhoPedidoService';
import { anexarDocumentosRequerimento } from '../core/services/documentoAnexoRequerimentoService';
import { gerarResumoRequerimentoPDF, RESUMO_REQUERIMENTO_DOC_ID } from '../core/services/resumoRequerimentoService';
import { gerarTitulo } from '../core/services/tituloService';
import { getContextoAcesso, isPedidoVisivelParaUsuario, getMotivoBloqueioPedido, getTipoInscricaoUsuario, adaptarConfigAoPerfil } from '../core/services/acessoPedidoService';
import { buildInitialValues, buildPessoaUpdatePayload, resolverMunicipioBuscaInicial, valoresDoCadastro } from '../core/services/perfilPrefillService';
import { consultarStatusSuplementar, SITUACAO_SUPLEMENTAR } from '../core/services/regularizeSuplementarService';
import FormStep from '../core/components/FormStep/index';
import DocumentStep from '../core/components/DocumentStep/index';
import PagamentoStep from '../core/components/PagamentoStep/index';
import SuccessScreen from '../core/components/SuccessScreen';
import AjudaFlutuante from '../core/components/AjudaFlutuante';
import RetomarPedidoCard from '../core/components/RetomarPedidoCard';
import NavegacaoPedido from '../core/components/NavegacaoPedido';
import { PageContainer, Container, Header, Title, Subtitle, StepsContainer, Step, StepNumber, StepLabel } from '../core/styles/wizardStyles';

// Pedidos sem nenhum documento configurado pulam a etapa de Documentos — que é
// onde o Resumo do Requerimento normalmente é gerado (useDocuments). Pra esses,
// o resumo é gerado aqui, na finalização, e anexado sozinho. Best-effort, como
// no useDocuments: se a geração falhar, o pedido segue sem o resumo.
const gerarResumoSemEtapaDocumentos = async ({ config, formData, nomeRequerente, numeroPedido, comTaxa }) => {
  try {
    const file = await gerarResumoRequerimentoPDF({ config, formData, nomeRequerente, numeroPedido, comTaxa });
    return { [RESUMO_REQUERIMENTO_DOC_ID]: file };
  } catch (err) {
    logger.warn('Não foi possível gerar o Resumo do Requerimento automaticamente:', err.message);
    return {};
  }
};

const DynamicPedidoPage = () => {
  const { departamentoId, pedidoId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Config ajustado a quem está logado (ex: PJ não vê os campos `apenasPF`, PF
  // inscrita não vê os `apenasNaoInscrito`). Memorizado: nesses casos é um
  // objeto novo, e vários efeitos dependem de `config`.
  const tipoInscricao = getTipoInscricaoUsuario(user);
  const oabData = authService.getOABData();
  const isInscrito = Boolean(oabData?.isInscrito);
  const config = useMemo(
    () => adaptarConfigAoPerfil(getPedidoConfig(departamentoId, pedidoId), tipoInscricao, user, isInscrito),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só nome/documento do login entram no config
    [departamentoId, pedidoId, tipoInscricao, isInscrito, user?.nome, user?.documento]
  );
  const autoFinalizarRef = useRef(false);
  const resumoAnexadoRef = useRef(false);
  const perfilInicialRef = useRef({});
  const perfilCarregadoRef = useRef(false);
  const aaspVerificadoRef = useRef(false);
  // undefined = ainda consultando (só quando o pedido declara regularizeSuplementarCheck);
  // pedidos sem essa flag já nascem com null, e o wizard nunca fica esperando por ela.
  const [aaspStatus, setAaspStatus] = useState(config?.regularizeSuplementarCheck ? undefined : null);

  // Retomada de pedido deixado pela metade (ver rascunhoPedidoService). Enquanto
  // não verifica se há rascunho, o wizard não aparece — senão o autosave
  // sobrescreveria o rascunho antigo com o formulário vazio.
  const rascunhoCarregadoRef = useRef(false);
  const [rascunhoVerificado, setRascunhoVerificado] = useState(false);
  const [rascunhoPendente, setRascunhoPendente] = useState(null);
  const [descartandoRascunho, setDescartandoRascunho] = useState(false);
  // Boleto/PIX já gerado: vai junto no rascunho pra não gerar uma segunda cobrança.
  const [pagamentoSalvo, setPagamentoSalvo] = useState(null);
  const pessoaId = getPessoaIdRascunho(user);
  // Último estado ainda não gravado pelo autosave (ver gravarSalvamentoPendente).
  const salvamentoPendenteRef = useRef(null);
  const gravarSalvamentoPendente = useCallback(() => {
    const pendente = salvamentoPendenteRef.current;
    if (!pendente) return;
    salvamentoPendenteRef.current = null;
    salvarRascunho(pendente.pessoaId, pendente.rascunho);
  }, []);
  const departamento = getDepartamento(departamentoId);
  const rotaPedidos = departamento ? `/requerimentos/${departamento.id}/pedidos` : '/home';

  const {
    currentStep,
    formData,
    errors,
    touched,
    documentos,
    loading,
    error,
    protocoloData,
    solicitacaoId,
    numeroPedido,
    requerimentoId,
    hasDocuments,
    isDocumentStep,
    isPagamentoStep,
    isSuccessStep,
    paymentAmount,
    dynamicPaymentConfig,
    setFormData,
    setDocumentos,
    setLoading,
    setError,
    setProtocoloData,
    setSolicitacaoId,
    setNumeroPedido,
    setRequerimentoId,
    setErrors,
    setTouched,
    handleChange,
    handleBlur,
    nextStep,
    prevStep,
    resetForm,
    restaurarRascunho
  } = usePedidoForm(config);

  const isentoPorAasp = aaspStatus === SITUACAO_SUPLEMENTAR.PAGO;
  const hasPayment = config?.paymentConfig && paymentAmount > 0 && !isentoPorAasp;

  // O aviso de erro fica no topo da página, mas quem disparou o envio está lá
  // embaixo, perto dos botões — sem rolar, parece que o botão "não fez nada".
  const erroRef = useRef(null);
  useEffect(() => {
    if (error) erroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  // Pedidos que declaram regularizeSuplementarCheck (hoje só Inscrição Suplementar)
  // consultam a API de Regularização Suplementar (parceria AASP) antes de exibir o
  // formulário: pessoa sem pendência (fora da base da AASP ou com
  // necessitaSuplementar=false) segue o fluxo normal (com taxa), pessoa que já
  // pagou a pendência com a AASP não paga taxa de novo aqui (vira hasPayment=false,
  // reaproveitando o fluxo "sem taxa" já existente), e pessoa com pendência em aberto
  // é bloqueada de solicitar o pedido. Falha na consulta (rede/timeout/5xx) não trava
  // o pedido — assume sem pendência e segue o fluxo normal com taxa.
  useEffect(() => {
    if (!config?.regularizeSuplementarCheck || aaspVerificadoRef.current || !user) return;
    // Não consulta a AASP se a pessoa nem pode acessar este pedido (ex: PJ ou
    // Advogado(a) já inscrito digitando a URL direto) — mesma regra usada no
    // render, senão dispara uma consulta real na AASP com o documento errado
    // antes da tela de "Pedido não encontrado" aparecer.
    if (!isPedidoVisivelParaUsuario({ id: pedidoId, categoria: config.categoria }, getContextoAcesso(user, oabData))) return;
    aaspVerificadoRef.current = true;

    (async () => {
      try {
        const { situacao } = await consultarStatusSuplementar(user.documento);
        setAaspStatus(situacao);
      } catch (err) {
        logger.warn('Não foi possível consultar a API de Regularização Suplementar, seguindo fluxo normal do pedido:', err.message);
        setAaspStatus(SITUACAO_SUPLEMENTAR.SEM_PENDENCIA);
      }
    })();
  }, [config, user, oabData, pedidoId]);

  // Campos travados com o dado do login (Nome da Sociedade e CNPJ pra PJ — ver
  // adaptarConfigAoPerfil): entram no formulário assim que o config fica pronto,
  // sem esperar o /auth/profile, e sempre prevalecem (o campo não é editável).
  useEffect(() => {
    const valores = valoresDoCadastro(config);
    if (Object.keys(valores).length > 0) setFormData(prev => ({ ...prev, ...valores }));
  }, [config, setFormData]);

  // Pré-preenche o formulário com os dados já cadastrados na OAB (GET /auth/profile),
  // restrito aos campos que este pedido realmente tem. Roda uma única vez por pedido
  // aberto; guarda os valores originais em `perfilInicialRef` pra servir de base de
  // comparação depois, em sincronizarDadosPessoa. Best-effort: se a busca falhar, o
  // usuário só preenche manualmente, como já acontecia antes desta feature.
  // Espera o usuário carregar: antes disso o config ainda é o de PF, e uma PJ
  // receberia campos que não são dela (ver adaptarConfigAoPerfil).
  useEffect(() => {
    if (!config || !user || perfilCarregadoRef.current) return;
    perfilCarregadoRef.current = true;

    (async () => {
      try {
        const { usuario } = await userProfileService.getProfile();
        const initialValues = buildInitialValues(usuario, config);
        // Naturalidade/Município do Título podem ter vindo como campo `municipioBusca`
        // (autocomplete) no config deste pedido — o /profile só devolve o nome em
        // texto, então resolve ID + rótulo de exibição à parte (ver perfilPrefillService).
        const municipioPatch = await resolverMunicipioBuscaInicial(initialValues, config);
        const valoresFinais = { ...initialValues, ...municipioPatch };
        perfilInicialRef.current = valoresFinais;
        setFormData(prev => ({ ...valoresFinais, ...prev }));
      } catch (err) {
        logger.warn('Não foi possível pré-preencher o formulário com o perfil:', err.message);
      }
    })();
  }, [config, user, setFormData]);

  const aplicarRascunho = (rascunho) => {
    restaurarRascunho(rascunho);
    setPagamentoSalvo(rascunho.pagamento || null);
    setRascunhoPendente(null);
  };

  // Ao abrir o pedido, procura um rascunho salvo. Se ele já tinha virado
  // requerimento, confere na API se ainda está Pendente — se foi concluído ou
  // cancelado (ex: em outra aba, ou descartado em Minhas Solicitações), o
  // rascunho local não vale mais. Vindo de "Continuar pedido" (?continuar=1)
  // retoma direto; senão pergunta se quer continuar ou começar de novo.
  useEffect(() => {
    if (!config || !user || rascunhoCarregadoRef.current) return;
    rascunhoCarregadoRef.current = true;

    const rascunho = obterRascunho(pessoaId, departamentoId, pedidoId);
    if (!rascunho) {
      setRascunhoVerificado(true);
      return;
    }

    (async () => {
      if (rascunho.solicitacaoId) {
        try {
          const statusResponse = await protocoloService.buscarStatusProtocolo(rascunho.solicitacaoId);
          const requerimento = (statusResponse.data || statusResponse)?.requerimento;
          if (!isRascunhoAberto(requerimento?.Situacao, requerimento?.DocumentoProtocolo)) {
            removerRascunho(pessoaId, departamentoId, pedidoId);
            setRascunhoVerificado(true);
            return;
          }
        } catch (err) {
          // Sem conseguir confirmar, oferece a retomada assim mesmo — na pior
          // das hipóteses a API recusa a finalização e a pessoa vê o erro.
          logger.warn('Não foi possível confirmar a situação do pedido salvo:', err.message);
        }
      }

      if (searchParams.get('continuar')) {
        aplicarRascunho(rascunho);
      } else {
        setRascunhoPendente(rascunho);
      }
      setRascunhoVerificado(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, user]);

  const handleDescartarRascunho = async () => {
    setDescartandoRascunho(true);
    setError(null);
    try {
      if (rascunhoPendente.requerimentoId) {
        await cancelarRequerimento(rascunhoPendente.requerimentoId);
      }
      removerRascunho(pessoaId, departamentoId, pedidoId);
      setRascunhoPendente(null);
    } catch (err) {
      setError(err.message || 'Não foi possível descartar o pedido anterior. Tente novamente.');
    } finally {
      setDescartandoRascunho(false);
    }
  };

  // Salva o progresso no navegador a cada mudança (com um pequeno atraso pra
  // não gravar a cada tecla). Só depois que a pessoa de fato mexeu em algo —
  // abrir o pedido e receber o pré-preenchimento do perfil não conta.
  useEffect(() => {
    if (!config || !pessoaId || !rascunhoVerificado || rascunhoPendente) return undefined;

    if (isSuccessStep) {
      salvamentoPendenteRef.current = null;
      removerRascunho(pessoaId, departamentoId, pedidoId);
      return undefined;
    }

    const houveProgresso = currentStep > 1 || requerimentoId || Object.keys(touched).length > 0;
    if (!houveProgresso) return undefined;

    // Pedido sem taxa: a etapa "Pagamento" é só a finalização automática, que
    // precisa dos anexos em memória — retomar ali finalizaria sem documentos.
    // Volta uma etapa (Documentos, ou a última do formulário).
    const etapaParaSalvar = !hasPayment && isPagamentoStep ? currentStep - 1 : currentStep;

    salvamentoPendenteRef.current = {
      pessoaId,
      rascunho: {
        departamentoId,
        pedidoId,
        currentStep: etapaParaSalvar,
        formData,
        solicitacaoId,
        numeroPedido,
        requerimentoId,
        pagamento: pagamentoSalvo
      }
    };
    const timer = setTimeout(gravarSalvamentoPendente, 400);
    return () => clearTimeout(timer);
  }, [config, pessoaId, departamentoId, pedidoId, rascunhoVerificado, rascunhoPendente, isSuccessStep, isPagamentoStep, hasPayment, currentStep, formData, touched, solicitacaoId, numeroPedido, requerimentoId, pagamentoSalvo, gravarSalvamentoPendente]);

  // Sair da página (Voltar, logo, menu) ou fechar a aba antes dos 400ms do
  // autosave não pode perder o que acabou de ser digitado: grava na hora.
  useEffect(() => {
    window.addEventListener('beforeunload', gravarSalvamentoPendente);
    return () => {
      window.removeEventListener('beforeunload', gravarSalvamentoPendente);
      gravarSalvamentoPendente();
    };
  }, [gravarSalvamentoPendente]);

  // Compara o formData atual com os valores originais do /profile e grava no cadastro
  // da pessoa (PUT /pessoa/:id) só o que o usuário de fato mudou. Best-effort: uma falha
  // aqui não pode travar o andamento do pedido, só deixa de sincronizar dessa vez.
  const sincronizarDadosPessoa = async () => {
    try {
      const payload = await buildPessoaUpdatePayload({
        config, formData, initialValues: perfilInicialRef.current
      });
      if (Object.keys(payload).length === 0) return;

      const { pessoaId } = protocoloService.extrairIdsUsuario(user);
      await pessoaService.atualizarDados(pessoaId, payload);
      perfilInicialRef.current = { ...perfilInicialRef.current, ...formData };
    } catch (err) {
      logger.warn('Não foi possível sincronizar os dados do cadastro:', err.message);
    }
  };

  // Última etapa do formulário concluída: aqui já temos o formData completo,
  // então dá pra resolver o modeloRequerimento (que pode depender de uma resposta
  // do formulário) e criar o rascunho do Requerimento (situacao 4) antes de avançar
  // pra etapa de Documentos. Se já existe um requerimentoId (ex: usuário voltou e
  // avançou de novo), não cria um segundo rascunho.
  const handleAvancarFormStep = async () => {
    await sincronizarDadosPessoa();

    if (currentStep !== config.steps.length || requerimentoId) {
      nextStep();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { solicitacaoId: sid, numeroPedido: numero, requerimentoId: rid } = await criarRascunhoRequerimento({
        config, formData, user, oabData
      });

      if (!rid) {
        setError('Erro ao obter ID do requerimento.');
        return;
      }

      setSolicitacaoId(sid);
      setNumeroPedido(numero);
      setRequerimentoId(rid);
      nextStep();
    } catch (err) {
      setError(err.message || 'Erro ao iniciar requerimento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPayment && isPagamentoStep && !isSuccessStep) {
      if (autoFinalizarRef.current) return;
      autoFinalizarRef.current = true;

      const autoFinalizar = async () => {
        setLoading(true);
        try {
          const { numeroProtocolo, documentoProtocoloId } = await finalizarRequerimentoSemTaxa({
            solicitacaoId, config, formData, user, oabData
          });

          const { usuarioId } = protocoloService.extrairIdsUsuario(user);
          const { tiposAssunto } = resolveProtocoloDados(config.protocoloConfig, formData, oabData);
          const documentosParaAnexar = hasDocuments
            ? documentos
            : await gerarResumoSemEtapaDocumentos({ config, formData, nomeRequerente: user?.nome, numeroPedido, comTaxa: false });

          await anexarDocumentosRequerimento({
            requerimentoId,
            protocoloId: documentoProtocoloId,
            usuarioId,
            tiposAssunto,
            documentConfig: config.documentConfig,
            documentos: documentosParaAnexar
          });

          const dataEnvio = new Date().toLocaleString('pt-BR');

          setProtocoloData({
            protocoloNumero: numeroProtocolo,
            dataEnvio,
            ...formData,
            documentos,
            pagamento: { isento: true, valor: 0 }
          });

          nextStep();
        } catch (err) {
          autoFinalizarRef.current = false;
          setError(err.message || 'Erro ao enviar requerimento');
        } finally {
          setLoading(false);
        }
      };

      autoFinalizar();
    }
  }, [hasPayment, hasDocuments, isPagamentoStep, isSuccessStep, config, formData, documentos, solicitacaoId, numeroPedido, requerimentoId, user, oabData, setLoading, setProtocoloData, nextStep, setError]);

  // Pedidos com taxa: o anexo é vinculado só ao Requerimento (nunca existe
  // Protocolo/DocumentoProtocolo pra esses) — já dá pra anexar assim que os
  // documentos são selecionados, sem esperar a etapa de Pagamento.
  const handleEnviarDocumentos = async (docs) => {
    setDocumentos(docs);

    if (!hasPayment) {
      nextStep();
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { usuarioId } = protocoloService.extrairIdsUsuario(user);
      const { tiposAssunto } = resolveProtocoloDados(config.protocoloConfig, formData, oabData);

      await anexarDocumentosRequerimento({
        requerimentoId,
        usuarioId,
        tiposAssunto,
        documentConfig: config.documentConfig,
        documentos: docs
      });

      nextStep();
    } catch (err) {
      setError(err.message || 'Erro ao enviar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarTitulo = async (valor, tipoPagamento) => {
    const tituloData = await gerarTitulo({ config, formData, user, valor, requerimentoId });
    setPagamentoSalvo({ tipoPagamento, tituloData });
    return tituloData;
  };

  const handleFinalizarComPagamento = async (dadosPagamento) => {
    setLoading(true);
    try {
      // Pedido com taxa sem etapa de Documentos (ex: Certidões): anexa o resumo
      // aqui, com o formulário já final. O ref evita anexar de novo se a
      // pessoa tentar outra vez depois de uma falha em marcarAguardandoPagamento.
      if (!hasDocuments && !resumoAnexadoRef.current) {
        const { usuarioId } = protocoloService.extrairIdsUsuario(user);
        const { tiposAssunto } = resolveProtocoloDados(config.protocoloConfig, formData, oabData);
        await anexarDocumentosRequerimento({
          requerimentoId,
          usuarioId,
          tiposAssunto,
          documentConfig: config.documentConfig,
          documentos: await gerarResumoSemEtapaDocumentos({ config, formData, nomeRequerente: user?.nome, numeroPedido, comTaxa: true })
        });
        resumoAnexadoRef.current = true;
      }

      await marcarAguardandoPagamento(requerimentoId);

      const dataEnvio = new Date().toLocaleString('pt-BR');
      const { opcaoSelecionada, opcaoNome, opcaoDescricao } = describePaymentSelection(config?.paymentConfig, formData);

      setProtocoloData({
        dataEnvio,
        ...formData,
        documentos,
        pagamento: {
          ...dadosPagamento,
          valor: paymentAmount,
          opcaoSelecionada,
          opcaoNome,
          opcaoDescricao
        }
      });

      nextStep();
    } catch (err) {
      setError(err.message || 'Erro ao enviar requerimento');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoRequerimento = () => {
    autoFinalizarRef.current = false;
    resumoAnexadoRef.current = false;
    setPagamentoSalvo(null);
    resetForm();
    setFormData(valoresDoCadastro(config));
  };

  if (!config) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <Header>
              <Title>Pedido não encontrado</Title>
              <Subtitle>O tipo de pedido solicitado não existe</Subtitle>
              <Button variant="primary" onClick={() => navigate(rotaPedidos)}>
                Voltar para requerimentos
              </Button>
            </Header>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <p>Carregando...</p>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  // Usa o pedidoId da URL (a mesma chave que getPedidosByDepartamento devolve
  // como "id" na listagem), não config.id — o campo interno do config.js às
  // vezes tem uma grafia diferente da chave registrada no barrel (ex:
  // "pedido_AlteracaoNome" vs "pedido_alteracao_nome"), e a regra de acesso
  // precisa da mesma identidade usada na tela de seleção.
  //
  // Vem antes da checagem de aaspStatus (abaixo): se o pedido nem for visível
  // pra essa pessoa, a consulta à AASP nunca dispara (ver useEffect acima) e
  // aaspStatus ficaria "undefined" pra sempre — checar visibilidade primeiro
  // evita a tela de "Carregando..." travada.
  const pedidoAcesso = { id: pedidoId, categoria: config.categoria };
  const contextoAcesso = getContextoAcesso(user, oabData);

  // Pedido aberto por link direto com o login errado (ex: PF num Distrato de
  // Sociedade, PJ numa Inscrição, Estagiário(a) num Licenciamento): explica
  // quem pode fazer o pedido, em vez do "Pedido não encontrado" genérico abaixo.
  const motivoBloqueio = getMotivoBloqueioPedido(pedidoAcesso, contextoAcesso);
  if (motivoBloqueio) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <Header>
              <Title>{config.nome}</Title>
            </Header>
            <Alert
              type="warning"
              message={motivoBloqueio}
            />
            <Button
              variant="primary"
              onClick={() => navigate(rotaPedidos)}
              style={{ marginTop: '1rem' }}
            >
              Voltar para requerimentos
            </Button>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  if (!isPedidoVisivelParaUsuario(pedidoAcesso, contextoAcesso)) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <Header>
              <Title>Pedido não encontrado</Title>
              <Subtitle>O tipo de pedido solicitado não existe</Subtitle>
              <Button variant="primary" onClick={() => navigate(rotaPedidos)}>
                Voltar para requerimentos
              </Button>
            </Header>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  if (aaspStatus === undefined) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <p>Carregando...</p>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  // Alguns pedidos (ex: Inscrição para Comissões) exigem que a pessoa esteja
  // adimplente com a OAB. Em vez de esconder o pedido da listagem, ele fica
  // visível e explica o motivo do bloqueio ao ser aberto.
  if (config.bloqueiaInadimplente && oabData?.isInadimplente) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <Header>
              <Title>{config.nome}</Title>
            </Header>
            <Alert
              type="error"
              message="Você está inadimplente com a OAB/MS. Regularize sua situação financeira para solicitar este pedido."
            />
            <Button
              variant="primary"
              onClick={() => navigate(rotaPedidos)}
              style={{ marginTop: '1rem' }}
            >
              Voltar para requerimentos
            </Button>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  // Pessoa notificada pela AASP com pendência ainda em aberto — não pode fazer
  // o pedido de forma alguma (nem com taxa, nem sem) até resolver com a AASP.
  if (aaspStatus === SITUACAO_SUPLEMENTAR.PENDENTE) {
    return (
      <MainLayout>
        <PageContainer>
          <Container>
            <Header>
              <Title>{config.nome}</Title>
            </Header>
            <Alert
              type="error"
              message={
                <>
                  Por favor, acesse o site da Regularize Suplementar pelo link{' '}
                  <a href="https://advogado.regularizesuplementar.com.br/auth/MS/entrar" target="_blank" rel="noopener noreferrer">
                    https://advogado.regularizesuplementar.com.br/auth/MS/entrar
                  </a>{' '}
                  e resolva a pendência antes de prosseguir com a inscrição. E-mail para dúvidas: suplementar@oabms.org.br
                </>
              }
            />
            <Button
              variant="primary"
              onClick={() => navigate(rotaPedidos)}
              style={{ marginTop: '1rem' }}
            >
              Voltar para requerimentos
            </Button>
          </Container>
        </PageContainer>
      </MainLayout>
    );
  }

  const mostrarWizard = rascunhoVerificado && !rascunhoPendente;

  return (
    <MainLayout>
      <PageContainer>
        <Container>
          <NavegacaoPedido
            nomeDepartamento={departamento?.nome}
            onVoltarPedidos={() => navigate(rotaPedidos)}
            onVoltarHome={() => navigate('/home')}
          />

          <Header>
            <Title>{config.nome}</Title>
            <Subtitle>{config.descricao}</Subtitle>
          </Header>

          {error && <Alert ref={erroRef} role="alert" type="error" message={error} onClose={() => setError(null)} style={{ marginBottom: '1rem' }} />}
          {isentoPorAasp && !isSuccessStep && (
            <Alert
              type="info"
              message="Identificamos que você já pagou a pendência com a AASP referente à Inscrição Suplementar — este pedido será enviado sem cobrança de taxa adicional."
              style={{ marginBottom: '1rem' }}
            />
          )}

          {!rascunhoVerificado && <p>Carregando...</p>}

          {rascunhoPendente && (
            <RetomarPedidoCard
              rascunho={rascunhoPendente}
              etapaDocumentos={hasDocuments ? config.steps.length + 1 : null}
              loading={descartandoRascunho}
              onContinuar={() => aplicarRascunho(rascunhoPendente)}
              onDescartar={handleDescartarRascunho}
            />
          )}

          {mostrarWizard && !isSuccessStep && (
            <StepsContainer>
              {config.steps.map((step, idx) => (
                <Step key={idx}>
                  <StepNumber
                    $active={currentStep === idx + 1}
                    $completed={currentStep > idx + 1}
                  >
                    {idx + 1}
                  </StepNumber>
                  <StepLabel $active={currentStep === idx + 1}>
                    {step.title}
                  </StepLabel>
                </Step>
              ))}

              {hasDocuments && (
                <Step>
                  <StepNumber
                    $active={isDocumentStep}
                    $completed={currentStep > config.steps.length + 1}
                  >
                    {config.steps.length + 1}
                  </StepNumber>
                  <StepLabel $active={isDocumentStep}>Documentos</StepLabel>
                </Step>
              )}

              {hasPayment && (
                <Step>
                  <StepNumber
                    $active={isPagamentoStep}
                    $completed={currentStep > config.steps.length + (hasDocuments ? 2 : 1)}
                  >
                    {config.steps.length + (hasDocuments ? 2 : 1)}
                  </StepNumber>
                  <StepLabel $active={isPagamentoStep}>Pagamento</StepLabel>
                </Step>
              )}
            </StepsContainer>
          )}

          {mostrarWizard && currentStep <= config.steps.length && (
            <FormStep
              step={config.steps[currentStep - 1]}
              formData={formData}
              errors={errors}
              touched={touched}
              onChange={handleChange}
              onBlur={handleBlur}
              onNext={handleAvancarFormStep}
              onPrev={prevStep}
              isFirst={currentStep === 1}
              isLast={currentStep === config.steps.length}
              loading={loading}
              setErrors={setErrors}
              setTouched={setTouched}
              nextLabelOnLast={
                hasDocuments ? 'Próximo: Documentos →' : hasPayment ? 'Próximo: Pagamento →' : 'Finalizar →'
              }
            />
          )}

          {mostrarWizard && isDocumentStep && (
            <DocumentStep
              formData={formData}
              loading={loading}
              onComplete={handleEnviarDocumentos}
              onBack={prevStep}
              config={config}
              pedidoName={config.nome}
              nomeRequerente={user?.nome}
              documentoRequerente={user?.documento}
              numeroPedido={numeroPedido}
              comTaxa={Boolean(hasPayment)}
            />
          )}

          {mostrarWizard && hasPayment && isPagamentoStep && (
            <PagamentoStep
              loading={loading}
              onComplete={handleFinalizarComPagamento}
              onGerarTitulo={handleGerarTitulo}
              onBack={prevStep}
              paymentConfig={dynamicPaymentConfig}
              pedidoName={config.nome}
              pagamentoInicial={pagamentoSalvo}
            />
          )}

          {isSuccessStep && (
            <SuccessScreen
              protocoloData={protocoloData}
              pedidoName={config.nome}
              numeroPedido={numeroPedido}
              onNovoRequerimento={handleNovoRequerimento}
            />
          )}

          {/* Botão "Ajuda" só existe em Seleção e Inscrição, e só nas etapas de formulário e de Documentos */}
          {mostrarWizard && departamentoId === 'selecao-inscricao' && (currentStep <= config.steps.length || isDocumentStep) && (
            <AjudaFlutuante etapa={isDocumentStep ? 'documentos' : 'formulario'} />
          )}
        </Container>
      </PageContainer>
    </MainLayout>
  );
};

export default DynamicPedidoPage;